<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import PerformanceAPI from 'dashboard/api/performance';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { messageStamp } from 'shared/helpers/timeHelper';
import { useAlert } from 'dashboard/composables';
import ReportScopeFilters from './components/ReportScopeFilters.vue';
import { formatReportDuration } from './helpers/reportFormatHelper';
import {
  generateCompleteURLParams,
  parseFilterURLParams,
  parseReportURLParams,
} from './helpers/reportFilterHelper';
import store from 'dashboard/store';
import CustomerServiceWorkspaceNav from 'dashboard/components-next/navigation/CustomerServiceWorkspaceNav.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const overview = ref(null);
const report = ref([]);
const loading = ref(true);
const loadError = ref(false);
const settingsSaving = ref(false);
const historyRecalculating = ref(false);
const exportingMode = ref('');
const showRecalculationConfirmation = ref(false);
const drilldown = ref({
  open: false,
  loading: false,
  error: false,
  records: [],
  definition: '',
  title: '',
});
const showSettings = ref(false);
const performanceSettings = ref({
  excluded_periods: [],
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const reportUrlParams = parseReportURLParams(route.query);
const scopeUrlParams = parseFilterURLParams(route.query);
const customDateRange = ref(
  reportUrlParams.from && reportUrlParams.to
    ? [
        new Date(reportUrlParams.from * 1000),
        new Date(reportUrlParams.to * 1000),
      ]
    : [subDays(new Date(), 6), new Date()]
);
const selectedDateRange = ref(
  reportUrlParams.range || DATE_RANGE_TYPES.LAST_7_DAYS
);
const agentId = ref(scopeUrlParams.agent_id);
const teamId = ref(scopeUrlParams.team_id);
const inboxId = ref(scopeUrlParams.inbox_id);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const reportColumns = computed(() =>
  [
    'AGENT',
    'HANDLED_CONVERSATIONS',
    'RESPONSE_TURNS',
    'UNPROCESSED_COUNT',
    'PROCESSED_UNRESOLVED_COUNT',
    'WARNING_COUNT',
    'TIMEOUT_COUNT',
    'TIMEOUT_RATE',
    'AVERAGE_REPLY',
    'MAX_REPLY',
    'MEDIAN_REPLY',
    'P90_REPLY',
    'REPLY_RATE',
    'OFFLINE_REASSIGNMENT_COUNT',
  ].map(key => ({
    key,
    label: t(`PERFORMANCE.${key}`),
    definition: t(`PERFORMANCE.COLUMN_DEFINITIONS.${key}`),
  }))
);
const metricItems = computed(() => [
  ['PROCESSED', 'processed', t('PERFORMANCE.METRICS.PROCESSED')],
  ['UNPROCESSED', 'unprocessed', t('PERFORMANCE.METRICS.UNPROCESSED')],
  [
    'PROCESSED_UNRESOLVED',
    'processed_unresolved',
    t('PERFORMANCE.METRICS.PROCESSED_UNRESOLVED'),
  ],
  ['REASSIGN', 'reassign', t('PERFORMANCE.METRICS.REASSIGN')],
]);
const dayGroupOptions = computed(() => [
  { value: 'all', label: t('PERFORMANCE.SETTINGS.DAYS.ALL') },
  { value: 'weekdays', label: t('PERFORMANCE.SETTINGS.DAYS.WEEKDAYS') },
  { value: 'weekends', label: t('PERFORMANCE.SETTINGS.DAYS.WEEKENDS') },
  { value: '1', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_1') },
  { value: '2', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_2') },
  { value: '3', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_3') },
  { value: '4', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_4') },
  { value: '5', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_5') },
  { value: '6', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_6') },
  { value: '7', label: t('PERFORMANCE.SETTINGS.DAYS.DAY_7') },
]);
const scheduleTypeOptions = computed(() => [
  {
    value: 'recurring',
    label: t('PERFORMANCE.SETTINGS.SCHEDULE_TYPES.RECURRING'),
  },
  {
    value: 'date_range',
    label: t('PERFORMANCE.SETTINGS.SCHEDULE_TYPES.DATE_RANGE'),
  },
]);
const scopeTypeOptions = computed(() => [
  { value: 'all', label: t('PERFORMANCE.SETTINGS.SCOPES.ALL') },
  { value: 'agents', label: t('PERFORMANCE.SETTINGS.SCOPES.AGENTS') },
]);

const duration = seconds => formatReportDuration(seconds, t);
const percent = value => `${Math.round(value * 100)}%`;
const metricValue = key =>
  ({
    PROCESSED: overview.value?.processed_count || 0,
    UNPROCESSED: overview.value?.unprocessed_count || 0,
    PROCESSED_UNRESOLVED: overview.value?.processed_unresolved_count || 0,
    REASSIGN: overview.value?.offline_reassignments || 0,
  })[key];

const closeDrilldown = () => {
  drilldown.value.open = false;
};

const conversationUrl = conversationId =>
  `/app/accounts/${route.params.accountId}/conversations/${conversationId}`;
const recordTitle = record =>
  `${record.contact_name || t('PERFORMANCE.DRILLDOWN.UNKNOWN_CONTACT')} #${record.display_id}`;
const recordTime = value =>
  value ? messageStamp(value, 'yyyy年M月d日 HH:mm:ss') : '';
const recordMeta = record =>
  [
    recordTime(record.occurred_at),
    record.agent_name || t('PERFORMANCE.DRILLDOWN.UNASSIGNED_AGENT'),
    record.team_name || t('PERFORMANCE.DRILLDOWN.UNASSIGNED_TEAM'),
    record.inbox_name || t('PERFORMANCE.DRILLDOWN.UNKNOWN_INBOX'),
  ].join(' · ');
const responseTimeline = record =>
  [
    `${t('PERFORMANCE.DRILLDOWN.CUSTOMER_MESSAGE_TIME')}：${recordTime(record.waiting_started_at)}`,
    record.replied_at &&
      `${t('PERFORMANCE.DRILLDOWN.AGENT_REPLY_TIME')}：${recordTime(record.replied_at)}`,
  ]
    .filter(Boolean)
    .join(' · ');
const excludedPeriodsText = periods =>
  periods
    .map(period => {
      const schedule =
        (period.schedule_type || 'recurring') === 'date_range'
          ? `${period.start_date}~${period.end_date}`
          : (period.days || []).join('/');
      const scope = period.agent_ids?.length
        ? `客服ID ${period.agent_ids.join('/')}`
        : t('PERFORMANCE.SETTINGS.SCOPES.ALL');
      return `${period.name || schedule} ${schedule} ${period.start_time}-${period.end_time}（${scope}）`;
    })
    .join('；');

const daysForGroup = group =>
  ({
    all: [1, 2, 3, 4, 5, 6, 7],
    weekdays: [1, 2, 3, 4, 5],
    weekends: [6, 7],
  })[group] || [Number(group)];

const groupForDays = days => {
  const key = [...days].sort().join(',');
  if (key === '1,2,3,4,5,6,7') return 'all';
  if (key === '1,2,3,4,5') return 'weekdays';
  if (key === '6,7') return 'weekends';
  return String(days[0] || 1);
};

const applySettings = data => {
  performanceSettings.value = {
    excluded_periods: (data.excluded_periods || []).map(period => ({
      ...period,
      schedule_type: period.schedule_type || 'recurring',
      day_group: groupForDays(period.days || []),
      scope_type:
        period.scope_type || (period.agent_ids?.length ? 'agents' : 'all'),
      agent_ids: period.agent_ids || [],
    })),
  };
};

const loadSettings = async () => {
  const response = await PerformanceAPI.settings();
  applySettings(response.data);
};

const addExcludedPeriod = () => {
  performanceSettings.value.excluded_periods.push({
    name: '',
    schedule_type: 'recurring',
    day_group: 'all',
    start_date: messageStamp(new Date(), 'yyyy-MM-dd'),
    end_date: messageStamp(new Date(), 'yyyy-MM-dd'),
    scope_type: 'all',
    agent_ids: [],
    start_time: '00:00',
    end_time: '08:00',
  });
};

const removeExcludedPeriod = index => {
  performanceSettings.value.excluded_periods.splice(index, 1);
};

const requestParams = computed(() => ({
  from: messageStamp(customDateRange.value[0], 'yyyy-MM-dd'),
  to: messageStamp(customDateRange.value[1], 'yyyy-MM-dd'),
  ...(agentId.value && { agent_id: agentId.value }),
  ...(teamId.value && { team_id: teamId.value }),
  ...(inboxId.value && { inbox_id: inboxId.value }),
}));

async function openDrilldown(metric, title, extraParams = {}) {
  drilldown.value = {
    open: true,
    loading: true,
    error: false,
    records: [],
    definition: '',
    title,
  };
  try {
    const response = await PerformanceAPI.drilldown({
      ...requestParams.value,
      metric,
      ...extraParams,
    });
    drilldown.value = {
      ...drilldown.value,
      loading: false,
      records: response.data.records || [],
      definition: response.data.definition || '',
    };
  } catch (error) {
    drilldown.value = { ...drilldown.value, loading: false, error: true };
  }
}

const syncFiltersToUrl = () => {
  router.replace({
    query: generateCompleteURLParams({
      from: getUnixStartOfDay(customDateRange.value[0]),
      to: getUnixEndOfDay(customDateRange.value[1]),
      range: selectedDateRange.value,
      filters: {
        agent_id: agentId.value,
        team_id: teamId.value,
        inbox_id: inboxId.value,
      },
    }),
  });
};

async function load() {
  loading.value = true;
  loadError.value = false;
  try {
    const [overviewResponse, reportResponse] = await Promise.all([
      PerformanceAPI.overview(requestParams.value),
      PerformanceAPI.report(requestParams.value),
    ]);
    overview.value = overviewResponse.data;
    report.value = reportResponse.data.agents || [];
  } catch (error) {
    overview.value = null;
    report.value = [];
    loadError.value = true;
  } finally {
    loading.value = false;
  }
}

const saveSettings = async () => {
  settingsSaving.value = true;
  try {
    const response = await PerformanceAPI.updateSettings({
      excluded_periods: performanceSettings.value.excluded_periods.map(
        period => {
          const schedule =
            period.schedule_type === 'date_range'
              ? {
                  start_date: period.start_date,
                  end_date: period.end_date,
                }
              : { days: daysForGroup(period.day_group) };
          return {
            name: period.name,
            schedule_type: period.schedule_type,
            scope_type: period.scope_type,
            agent_ids: period.scope_type === 'agents' ? period.agent_ids : [],
            start_time: period.start_time,
            end_time: period.end_time,
            ...schedule,
          };
        }
      ),
    });
    applySettings(response.data);
    showSettings.value = false;
    useAlert(t('PERFORMANCE.SETTINGS.SAVE_SUCCESS'));
    await load();
  } catch (error) {
    useAlert(
      error.response?.data?.message || t('PERFORMANCE.SETTINGS.SAVE_ERROR')
    );
  } finally {
    settingsSaving.value = false;
  }
};

const recalculateHistory = async () => {
  historyRecalculating.value = true;
  try {
    const response = await PerformanceAPI.recalculate({
      from: requestParams.value.from,
      to: requestParams.value.to,
    });
    showRecalculationConfirmation.value = false;
    useAlert(
      t('PERFORMANCE.SETTINGS.RECALCULATE_SUCCESS', {
        count: response.data.rebuilt_count,
      })
    );
    await load();
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('PERFORMANCE.SETTINGS.RECALCULATE_ERROR')
    );
  } finally {
    historyRecalculating.value = false;
  }
};

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  syncFiltersToUrl();
  load();
};

const onScopeFilterChange = filters => {
  agentId.value = filters.agentId;
  teamId.value = filters.teamId;
  inboxId.value = filters.inboxId;
  syncFiltersToUrl();
  load();
};

const download = async mode => {
  exportingMode.value = mode;
  try {
    const response = await PerformanceAPI.export({
      ...requestParams.value,
      mode,
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    const label = mode === 'daily' ? '每日明细' : '区间汇总';
    link.href = url;
    link.download = `客服绩效${label}-${requestParams.value.from}-${requestParams.value.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('PERFORMANCE.EXPORT_ERROR'));
  } finally {
    exportingMode.value = '';
  }
};

onMounted(async () => {
  await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('inboxes/get'),
    loadSettings(),
  ]);
  syncFiltersToUrl();
  load();
});
</script>

<template>
  <div class="space-y-4 py-5">
    <header class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ $t('PERFORMANCE.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('PERFORMANCE.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="isAdmin"
          type="button"
          class="button button--secondary"
          @click="showSettings = !showSettings"
        >
          {{ $t('PERFORMANCE.SETTINGS.BUTTON') }}
        </button>
        <button
          type="button"
          class="button button--secondary"
          :disabled="loading || Boolean(exportingMode)"
          @click="download('summary')"
        >
          {{ $t('PERFORMANCE.EXPORT_SUMMARY') }}
        </button>
        <button
          type="button"
          class="button button--primary"
          :disabled="loading || Boolean(exportingMode)"
          @click="download('daily')"
        >
          {{ $t('PERFORMANCE.EXPORT_DAILY') }}
        </button>
      </div>
    </header>

    <CustomerServiceWorkspaceNav
      active-page="performance"
      show-management-pages
    />

    <section class="rounded-lg border border-n-weak bg-n-surface-2 px-4 py-3">
      <p class="text-sm font-medium text-n-slate-12">
        {{ $t('PERFORMANCE.RESPONSIBILITY_TITLE') }}
      </p>
      <p class="mt-1 text-sm leading-6 text-n-slate-10">
        {{ $t('PERFORMANCE.RESPONSIBILITY_DESCRIPTION') }}
      </p>
    </section>

    <section
      v-if="showSettings && isAdmin"
      class="space-y-4 rounded-lg border border-n-weak bg-n-surface-2 p-4"
    >
      <div>
        <h2 class="font-medium text-n-slate-12">
          {{ $t('PERFORMANCE.SETTINGS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('PERFORMANCE.SETTINGS.DESCRIPTION') }}
        </p>
      </div>
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('PERFORMANCE.SETTINGS.EXCLUDED_TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-9">
              {{ $t('PERFORMANCE.SETTINGS.EXCLUDED_DESCRIPTION') }}
            </p>
          </div>
          <button
            type="button"
            class="button button--secondary button--small"
            @click="addExcludedPeriod"
          >
            {{ $t('PERFORMANCE.SETTINGS.ADD_PERIOD') }}
          </button>
        </div>
        <div
          v-for="(period, index) in performanceSettings.excluded_periods"
          :key="index"
          class="space-y-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <div class="grid gap-3 md:grid-cols-3">
            <label class="text-xs text-n-slate-10">
              {{ $t('PERFORMANCE.SETTINGS.RULE_NAME') }}
              <input
                v-model="period.name"
                class="mt-1 w-full"
                type="text"
                :placeholder="$t('PERFORMANCE.SETTINGS.RULE_NAME_PLACEHOLDER')"
              />
            </label>
            <label class="text-xs text-n-slate-10">
              {{ $t('PERFORMANCE.SETTINGS.SCHEDULE_TYPE') }}
              <select v-model="period.schedule_type" class="mt-1 w-full">
                <option
                  v-for="option in scheduleTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <label class="text-xs text-n-slate-10">
              {{ $t('PERFORMANCE.SETTINGS.APPLICABLE_SCOPE') }}
              <select v-model="period.scope_type" class="mt-1 w-full">
                <option
                  v-for="option in scopeTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>
          <div class="grid items-end gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
            <label
              v-if="period.schedule_type === 'recurring'"
              class="text-xs text-n-slate-10"
            >
              {{ $t('PERFORMANCE.SETTINGS.APPLICABLE_DAYS') }}
              <select v-model="period.day_group" class="mt-1 w-full">
                <option
                  v-for="option in dayGroupOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <div v-else class="grid grid-cols-2 gap-2">
              <label class="text-xs text-n-slate-10">
                {{ $t('PERFORMANCE.SETTINGS.START_DATE') }}
                <input
                  v-model="period.start_date"
                  class="mt-1 w-full"
                  type="date"
                />
              </label>
              <label class="text-xs text-n-slate-10">
                {{ $t('PERFORMANCE.SETTINGS.END_DATE') }}
                <input
                  v-model="period.end_date"
                  class="mt-1 w-full"
                  type="date"
                />
              </label>
            </div>
            <label class="text-xs text-n-slate-10">
              {{ $t('PERFORMANCE.SETTINGS.START_TIME') }}
              <input
                v-model="period.start_time"
                class="mt-1 w-full"
                type="time"
              />
            </label>
            <label class="text-xs text-n-slate-10">
              {{ $t('PERFORMANCE.SETTINGS.END_TIME') }}
              <input
                v-model="period.end_time"
                class="mt-1 w-full"
                type="time"
              />
            </label>
            <button
              type="button"
              class="button button--clear button--small"
              @click="removeExcludedPeriod(index)"
            >
              {{ $t('PERFORMANCE.SETTINGS.REMOVE') }}
            </button>
          </div>
          <label
            v-if="period.scope_type === 'agents'"
            class="block text-xs text-n-slate-10"
          >
            {{ $t('PERFORMANCE.SETTINGS.SELECT_AGENTS') }}
            <select
              v-model="period.agent_ids"
              class="mt-1 min-h-24 w-full"
              multiple
            >
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
          <p class="text-xs text-n-slate-9">
            {{ $t('PERFORMANCE.SETTINGS.OVERLAP_NOTE') }}
          </p>
        </div>
        <p
          v-if="!performanceSettings.excluded_periods.length"
          class="text-sm text-n-slate-9"
        >
          {{ $t('PERFORMANCE.SETTINGS.NO_EXCLUDED_PERIODS') }}
        </p>
      </div>
      <div class="flex justify-end">
        <button
          type="button"
          class="button button--primary"
          :disabled="settingsSaving"
          @click="saveSettings"
        >
          {{ $t('PERFORMANCE.SETTINGS.SAVE') }}
        </button>
      </div>
      <p class="text-xs text-n-slate-9">
        {{ $t('PERFORMANCE.SETTINGS.NEW_RULES_NOTE') }}
      </p>
      <div class="space-y-3 border-t border-n-weak pt-4">
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('PERFORMANCE.SETTINGS.RECALCULATE_TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-9">
            {{ $t('PERFORMANCE.SETTINGS.RECALCULATE_DESCRIPTION') }}
          </p>
        </div>
        <button
          v-if="!showRecalculationConfirmation"
          type="button"
          class="button button--secondary"
          :disabled="settingsSaving || historyRecalculating"
          @click="showRecalculationConfirmation = true"
        >
          {{ $t('PERFORMANCE.SETTINGS.RECALCULATE_BUTTON') }}
        </button>
        <div
          v-else
          class="space-y-3 rounded-md border border-n-weak bg-n-surface-1 p-3"
        >
          <p class="text-sm text-n-slate-11">
            {{
              $t('PERFORMANCE.SETTINGS.RECALCULATE_CONFIRM', {
                from: requestParams.from,
                to: requestParams.to,
              })
            }}
          </p>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="button button--clear"
              :disabled="historyRecalculating"
              @click="showRecalculationConfirmation = false"
            >
              {{ $t('PERFORMANCE.SETTINGS.CANCEL') }}
            </button>
            <button
              type="button"
              class="button button--primary"
              :disabled="historyRecalculating"
              @click="recalculateHistory"
            >
              {{ $t('PERFORMANCE.SETTINGS.RECALCULATE_CONFIRM_BUTTON') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <div class="flex flex-col gap-2 lg:flex-row lg:items-center">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <ReportScopeFilters
        :agent-id="agentId"
        :team-id="teamId"
        :inbox-id="inboxId"
        :agents="agents"
        :teams="teams"
        :inboxes="inboxes"
        :disabled="loading"
        @filter-change="onScopeFilterChange"
      />
    </div>

    <div
      v-if="loading && !overview"
      class="py-10 text-center text-sm text-n-slate-10"
    >
      {{ $t('PERFORMANCE.LOADING') }}
    </div>
    <section
      v-else-if="loadError"
      role="alert"
      class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-5 py-8 text-center"
    >
      <p class="text-sm font-medium text-n-ruby-11">
        {{ $t('PERFORMANCE.LOAD_ERROR') }}
      </p>
      <button type="button" class="button button--secondary mt-4" @click="load">
        {{ $t('PERFORMANCE.RETRY') }}
      </button>
    </section>
    <template v-else>
      <section class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div
          v-for="item in metricItems"
          :key="item[0]"
          class="cursor-pointer rounded-lg border border-n-weak bg-n-surface-2 p-4 hover:border-n-brand"
          role="button"
          tabindex="0"
          @click="openDrilldown(item[1], item[2])"
          @keydown.enter="openDrilldown(item[1], item[2])"
        >
          <p class="text-xs text-n-slate-10">
            {{ item[2] }}
          </p>
          <strong class="mt-1 block text-2xl text-n-slate-12">{{
            metricValue(item[0])
          }}</strong>
        </div>
      </section>

      <section
        class="overflow-x-auto rounded-lg border border-n-weak bg-n-surface-2"
      >
        <div
          class="border-b border-n-weak px-4 py-3 text-sm font-medium text-n-slate-12"
        >
          {{ $t('PERFORMANCE.REPORT') }}
        </div>
        <table class="w-full min-w-[1900px] text-left text-sm">
          <thead class="bg-n-slate-2 text-xs text-n-slate-10">
            <tr>
              <th
                v-for="column in reportColumns"
                :key="column.key"
                class="px-4 py-3"
              >
                <span class="inline-flex items-center gap-1">
                  <span>{{ column.label }}</span>
                  <Icon
                    v-tooltip.bottom="column.definition"
                    icon="i-lucide-info"
                    class="size-3.5 shrink-0 cursor-help text-n-slate-9"
                    tabindex="0"
                    :aria-label="column.definition"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in report"
              :key="row.user_id"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-3 font-medium text-n-slate-12">
                {{ row.name }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'processed',
                      $t('PERFORMANCE.HANDLED_CONVERSATIONS'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ row.handled_conversation_count }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'response_turn',
                      $t('PERFORMANCE.RESPONSE_TURNS'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ row.response_turn_count }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'unprocessed',
                      $t('PERFORMANCE.UNPROCESSED_COUNT'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ row.unprocessed_count }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'processed_unresolved',
                      $t('PERFORMANCE.PROCESSED_UNRESOLVED_COUNT'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ row.processed_unresolved_count }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown('warning', $t('PERFORMANCE.WARNING_COUNT'), {
                      agent_id: row.user_id,
                    })
                  "
                >
                  {{ row.warning_count }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown('timeout', $t('PERFORMANCE.TIMEOUT_COUNT'), {
                      agent_id: row.user_id,
                    })
                  "
                >
                  {{ row.timeout_count }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown('timeout', $t('PERFORMANCE.TIMEOUT_RATE'), {
                      agent_id: row.user_id,
                    })
                  "
                >
                  {{ percent(row.timeout_rate) }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'response_turn',
                      $t('PERFORMANCE.AVERAGE_REPLY'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ duration(row.average_reply_seconds) }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'response_turn',
                      $t('PERFORMANCE.MAX_REPLY'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ duration(row.max_reply_seconds) }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'response_turn',
                      $t('PERFORMANCE.MEDIAN_REPLY'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ duration(row.median_reply_seconds) }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'response_turn',
                      $t('PERFORMANCE.P90_REPLY'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ duration(row.percentile_90_reply_seconds) }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'response_turn',
                      $t('PERFORMANCE.REPLY_RATE'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ percent(row.reply_rate) }}
                </button>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                <button
                  class="text-n-brand hover:underline"
                  @click="
                    openDrilldown(
                      'reassign',
                      $t('PERFORMANCE.OFFLINE_REASSIGNMENT_COUNT'),
                      { agent_id: row.user_id }
                    )
                  "
                >
                  {{ row.offline_reassignment_count }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <div
        v-if="drilldown.open"
        class="fixed inset-0 z-50 flex justify-end bg-n-alpha-black2"
        @click.self="closeDrilldown"
      >
        <section
          class="h-full w-full max-w-2xl overflow-y-auto bg-n-background p-5 shadow-xl"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak pb-4"
          >
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{
                  $t('PERFORMANCE.DRILLDOWN.TITLE', { metric: drilldown.title })
                }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-10">
                {{ drilldown.definition }}
              </p>
              <p class="mt-1 text-xs text-n-slate-9">
                {{ $t('PERFORMANCE.DRILLDOWN.TIMEZONE_NOTE') }}
              </p>
            </div>
            <button class="button button--clear" @click="closeDrilldown">
              {{ $t('PERFORMANCE.DRILLDOWN.CLOSE') }}
            </button>
          </header>
          <p v-if="drilldown.loading" class="py-10 text-center text-n-slate-10">
            {{ $t('PERFORMANCE.DRILLDOWN.LOADING') }}
          </p>
          <div
            v-else-if="drilldown.error"
            class="my-6 rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-5 text-center text-n-ruby-11"
          >
            {{ $t('PERFORMANCE.DRILLDOWN.LOAD_ERROR') }}
          </div>
          <p
            v-else-if="!drilldown.records.length"
            class="py-10 text-center text-n-slate-10"
          >
            {{ $t('PERFORMANCE.DRILLDOWN.EMPTY') }}
          </p>
          <div v-else class="mt-4 space-y-3">
            <article
              v-for="record in drilldown.records"
              :key="`${record.conversation_id}-${record.occurred_at}`"
              class="rounded-lg border border-n-weak bg-n-surface-2 p-4"
            >
              <div class="flex items-center justify-between gap-3">
                <strong class="text-n-slate-12">
                  {{ recordTitle(record) }}
                </strong>
                <a
                  :href="conversationUrl(record.conversation_id)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-n-brand hover:underline"
                >
                  {{ $t('PERFORMANCE.DRILLDOWN.VIEW_CONVERSATION') }}
                </a>
              </div>
              <p class="mt-1 text-xs text-n-slate-9">
                {{ recordMeta(record) }}
              </p>
              <p
                v-if="record.waiting_started_at"
                class="mt-2 text-xs text-n-slate-10"
              >
                {{ responseTimeline(record) }}
              </p>
              <p class="mt-3 text-sm leading-6 text-n-slate-11">
                {{ record.explanation }}
              </p>
              <p
                v-if="record.wall_wait_seconds !== undefined"
                class="mt-2 text-xs text-n-slate-9"
              >
                {{
                  $t('PERFORMANCE.DRILLDOWN.WAIT_DETAIL', {
                    wall: duration(record.wall_wait_seconds),
                    effective: duration(record.effective_wait_seconds),
                    excluded: duration(record.excluded_wait_seconds),
                  })
                }}
              </p>
              <p
                v-if="record.excluded_periods?.length"
                class="mt-1 text-xs text-n-slate-9"
              >
                {{
                  $t('PERFORMANCE.DRILLDOWN.EXCLUDED_PERIODS', {
                    periods: excludedPeriodsText(record.excluded_periods),
                  })
                }}
              </p>
            </article>
          </div>
        </section>
      </div>

      <section class="rounded-lg border border-n-weak bg-n-surface-2 p-4">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ $t('PERFORMANCE.DEFINITIONS.TITLE') }}
        </h2>
        <ul class="mt-2 space-y-1 text-sm text-n-slate-10">
          <li>{{ $t('PERFORMANCE.DEFINITIONS.PROCESSED') }}</li>
          <li>{{ $t('PERFORMANCE.DEFINITIONS.TIMING') }}</li>
          <li>{{ $t('PERFORMANCE.DEFINITIONS.EXCLUSIONS') }}</li>
          <li>{{ $t('PERFORMANCE.DEFINITIONS.RESOLVED') }}</li>
        </ul>
      </section>
    </template>
  </div>
</template>
